import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AppState, Address, Plan } from './types';
import { AppliedPromo } from './config/promos';
import AddressStep from './components/AddressStep';
import SelectAddressStep from './components/SelectAddressStep';
import PlansStep from './components/PlansStep';
import DetailsStep from './components/DetailsStep';
import ConfirmationStep from './components/ConfirmationStep';
import { resolveAddress, checkQualification, createLead } from './services/api';

declare global {
  interface Window {
    grecaptcha: any;
  }
}

function readAddressFromLocationSearch(): { enteredAddress: string; googlePlaceId: string; enteredEmail: string } {
  if (typeof window === 'undefined') {
    return { enteredAddress: '', googlePlaceId: '', enteredEmail: '' };
  }
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get('address');
  const enteredAddress = (fromQuery ?? '').trim();
  const googlePlaceId = (params.get('place_id') ?? '').trim();
  // Supplied by the address widget on the marketing site. The field is still
  // shown on the address step, so a missing param just means it starts empty.
  const enteredEmail = (params.get('email') ?? '').trim();
  return { enteredAddress, googlePlaceId, enteredEmail };
}

const AppContent: React.FC = () => {
  const querySeed = readAddressFromLocationSearch();
  const [state, setState] = useState<AppState>({
    currentStep: 1,
    customerType: null,
    enteredAddress: querySeed.enteredAddress,
    googlePlaceId: querySeed.googlePlaceId,
    enteredEmail: querySeed.enteredEmail,
    addressOptions: [],
    selectedAddress: null,
    qualificationResult: null,
    selectedPlan: null,
    isServiceable: true,
    customer: {
      first_name: '',
      last_name: '',
      email: querySeed.enteredEmail,
      phone: '',
      contact_preference: 'email',
      referral_source: '',
      marketing_opt_in: true,
      terms_accepted: false,
      router_required: false,
    },
    promoCode: '',
    appliedPromo: null,
    captchaToken: '',
    loading: false,
    error: null,
    leadReference: null,
    confirmationMessage: null,
  });
  const [showAddressSelection, setShowAddressSelection] = useState(false);

  // Auto-submit address (used by URL params and postMessage)
  const autoSubmitAddress = useCallback((address: string, placeId?: string) => {
    if (!address) return;
    //console.log('Auto-submitting address:', { address, placeId });
    //handleAddressSubmit(address, placeId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Read address from URL params (e.g. /signup?address=123+Main+St&place_id=ChIJ...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const address = params.get('address');
    const placeId = params.get('place_id');
    if (address) {
      autoSubmitAddress(address, placeId || undefined);
    }
  }, [autoSubmitAddress]);

  // Listen for postMessage from parent window (iframe embedding)
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.data && e.data.type === 'crisp-signup-address') {
        autoSubmitAddress(e.data.address, e.data.place_id);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [autoSubmitAddress]);

  // Tell parent page to scroll to iframe top when step changes
  useEffect(() => {
    if (window.parent !== window) {
      window.parent.postMessage({ type: 'crisp-signup-scroll', step: state.currentStep }, '*');
    }
  }, [state.currentStep]);

  // Handle customer type change
  const handleCustomerTypeChange = (type: 'residential' | 'business') => {
    setState(prev => ({
      ...prev,
      customerType: type,
    }));
  };

  // Handle address submission
  const handleAddressSubmit = async (address: string, placeId?: string, email?: string) => {
    const submittedEmail = (email ?? state.enteredEmail).trim();
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      console.log('Submitting address:', { address, placeId });
      const result = await resolveAddress(address, placeId);
      console.log('Address resolution result:', result);

      if (result.success) {
        if (result.count > 0) {
          // Store addresses
          setState(prev => ({
            ...prev,
            enteredAddress: address,
            googlePlaceId: placeId || '',
            enteredEmail: submittedEmail,
            customer: { ...prev.customer, email: prev.customer.email || submittedEmail },
            addressOptions: result.addresses,
            loading: false,
          }));

          // If multiple addresses, show selection UI
          // If only one address, automatically proceed with qualification
          if (result.count > 1) {
            setShowAddressSelection(true);
          } else {
            const selectedAddr = result.addresses[0];
            setState(prev => ({ ...prev, selectedAddress: selectedAddr, loading: true }));
            handleAddressSelect(selectedAddr, submittedEmail);
          }
        } else {
          console.error('No addresses found');
          setState(prev => ({
            ...prev,
            error: result.message || 'No matching addresses found. Please try a different address.',
            loading: false
          }));
        }
      } else {
        console.error('Address resolution failed:', result);
        setState(prev => ({
          ...prev,
          error: result.message || 'Failed to resolve address. Please try again.',
          loading: false
        }));
      }
    } catch (err: any) {
      console.error('Address submission error:', err);
      setState(prev => ({
        ...prev,
        error: err.message || 'Failed to resolve address. Please try again.',
        loading: false
      }));
    }
  };

  // Handle address selection
  const handleAddressSelect = async (address: Address, emailOverride?: string) => {
    const email = (emailOverride ?? state.enteredEmail).trim();
    setState(prev => ({ ...prev, selectedAddress: address, loading: true, error: null }));
    
    try {
      // Ensure we have coordinates
      if (!address.latitude || !address.longitude) {
        throw new Error('Address coordinates are missing. Please try a different address.');
      }

      const qualifyParams = {
        id: address.id,
        lat: address.latitude,
        lng: address.longitude,
        email,
        customerType: state.customerType || 'residential',
        address: {
          formatted_address: address.formatted_address,
          suburb: address.suburb,
          state: address.state,
          postcode: address.postcode,
        },
      };

      let result: Awaited<ReturnType<typeof checkQualification>>;
      try {
        result = await checkQualification(
          qualifyParams.id, qualifyParams.lat, qualifyParams.lng,
          qualifyParams.email, qualifyParams.customerType, qualifyParams.address
        );
      } catch (firstError) {
        console.warn('First qualification attempt failed, retrying...', firstError);
        // Show the user we're still working on it
        setState(prev => ({
          ...prev,
          error: 'Still checking your address — hang tight...',
        }));

        // Wait briefly then retry
        await new Promise(resolve => setTimeout(resolve, 2000));

        result = await checkQualification(
          qualifyParams.id, qualifyParams.lat, qualifyParams.lng,
          qualifyParams.email, qualifyParams.customerType, qualifyParams.address
        );
      }
      
      // When both NBN and CRISP fail, skip the Plans step entirely and take the
      // customer straight to the details form for a manual qualification lead.
      const manualQualification = !result.serviceable && !!result.manual_qualification_required;

      setState(prev => ({
        ...prev,
        qualificationResult: result,
        isServiceable: result.serviceable,
        selectedPlan: null,
        currentStep: manualQualification ? 3 : 2,
        loading: false,
        error: null,
      }));
    } catch (error) {
      console.error('Error checking service qualification:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to check service qualification. Please try again.',
        loading: false,
      }));
    }
  };

  // Handle plan selection — clear any applied promo since eligibility/price
  // depends on the chosen plan and must be re-validated.
  const handlePlanSelect = (plan: Plan | null) => {
    setState(prev => ({
      ...prev,
      selectedPlan: plan,
      promoCode: '',
      appliedPromo: null,
      currentStep: 3,
    }));
  };

  // Promo code handlers (validation lives in DetailsStep via config/promos)
  const handleApplyPromo = (code: string, promo: AppliedPromo) => {
    setState(prev => ({ ...prev, promoCode: code, appliedPromo: promo }));
  };

  const handleRemovePromo = () => {
    setState(prev => ({ ...prev, promoCode: '', appliedPromo: null }));
  };

  // Handle lead submission
  const handleLeadSubmit = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      // Get reCAPTCHA token
      const token = await window.grecaptcha.execute(
        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
        { action: 'submit_lead' }
      );
      
      const leadData = {
        location_id: state.selectedAddress!.id,
        plan_id: state.selectedPlan?.plan_id || null,
        serviceable: state.isServiceable,
        customer_type: state.customerType || 'residential',
        // Prefer the selected plan's provider/technology (a plan has a single provider),
        // falling back to the qualification-level values for backwards compatibility.
        provider: state.selectedPlan?.provider
          || (state.qualificationResult?.provider === 'combined' ? undefined : state.qualificationResult?.provider)
          || 'manual_review',
        technology: state.selectedPlan?.technology ?? state.qualificationResult?.technology ?? undefined,
        // Mark this lead as an FTTP upgrade request if the chosen plan is an upgrade plan.
        fttp_upgrade: !!state.selectedPlan?.is_fttp_upgrade,
        // Flag manual-qualification leads (both NBN and CRISP automated checks failed)
        // so the lead clearly records why, for the CRISP team to review.
        qualification_failed: !state.isServiceable && !!state.qualificationResult?.manual_qualification_required,
        nbn_address_resolved: state.qualificationResult?.nbn_address_resolved ?? false,
        promo_code: state.appliedPromo?.code || null,
        customer: state.customer,
        address: {
          formatted_address: state.selectedAddress!.formatted_address,
          latitude: state.selectedAddress!.latitude!,
          longitude: state.selectedAddress!.longitude!,
          suburb: state.selectedAddress!.suburb || null,
          state: state.selectedAddress!.state || null,
          postcode: state.selectedAddress!.postcode || null,
        },
        captcha_token: token,
      };
      
      console.log('Submitting lead data:', leadData);
      
      const result = await createLead(leadData);
      
      if (result.success) {
        setState(prev => ({
          ...prev,
          leadReference: result.lead_id || null,
          confirmationMessage: result.message || null,
          currentStep: 4,
          loading: false,
        }));
      } else {
        setState(prev => ({
          ...prev,
          error: result.error || 'Failed to submit order',
          loading: false,
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to submit order. Please try again.',
        loading: false,
      }));
    }
  };

  // Update customer details
  const updateCustomer = (field: keyof typeof state.customer, value: string | boolean) => {
    setState(prev => ({
      ...prev,
      customer: { ...prev.customer, [field]: value },
    }));
  };

  // Go back to previous step
  const goBack = () => {
    if (state.currentStep === 2) {
      setState(prev => ({ ...prev, currentStep: 1, selectedAddress: null, qualificationResult: null }));
    } else if (state.currentStep === 3) {
      // Manual-qualification leads skip the Plans step, so Back returns to the
      // address entry rather than an empty Plans screen.
      const skippedPlans = !state.isServiceable && !!state.qualificationResult?.manual_qualification_required;
      if (skippedPlans) {
        setState(prev => ({
          ...prev,
          currentStep: 1,
          selectedAddress: null,
          qualificationResult: null,
          selectedPlan: null,
        }));
      } else {
        setState(prev => ({ ...prev, currentStep: 2, selectedPlan: null, promoCode: '', appliedPromo: null }));
      }
    }
  };

  // Go back from address selection
  const goBackFromAddressSelection = () => {
    setShowAddressSelection(false);
    setState(prev => ({
      ...prev,
      addressOptions: [],
      enteredAddress: '',
      googlePlaceId: '',
      error: null,
    }));
  };

  return (
    <div className="w-full">
      {/* Progress Bar */}
      <div className="bg-white py-4 px-4">
        <div className="mx-auto max-w-4xl">
          {/* Circles and Lines */}
          <div className="flex items-center justify-between mb-2">
            {[1, 2, 3, 4].map((step) => (
              <React.Fragment key={step}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0 ${
                    state.currentStep >= step
                      ? 'bg-crisp-orange text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step}
                </div>
                {step < 4 && (
                  <div
                    className={`h-1 flex-1 ${
                      state.currentStep > step ? 'bg-crisp-orange' : 'bg-gray-200'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
          {/* Labels */}
          <div className="flex justify-between">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex-1 text-center" style={{ maxWidth: '40px' }}>
                <span className="text-xs text-gray-700">
                  {step === 1 ? 'Address' : step === 2 ? 'Plans' : step === 3 ? 'Details' : 'Confirm'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 py-6">
        {state.error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 text-red-900 px-6 py-4 rounded-lg shadow-md animate-shake" role="alert">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="font-semibold">Error</p>
                <p className="text-sm mt-1">{state.error}</p>
              </div>
              <button 
                onClick={() => setState(prev => ({ ...prev, error: null }))}
                className="ml-auto flex-shrink-0 text-red-500 hover:text-red-700"
                aria-label="Dismiss error"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {state.currentStep === 1 && !showAddressSelection && (
          <AddressStep
            onSubmit={handleAddressSubmit}
            loading={state.loading}
            initialValue={state.enteredAddress}
            initialEmail={state.enteredEmail}
            prefilledPlaceId={state.googlePlaceId}
            customerType={state.customerType}
            onCustomerTypeChange={handleCustomerTypeChange}
          />
        )}

        {state.currentStep === 1 && showAddressSelection && (
          <SelectAddressStep
            addresses={state.addressOptions}
            onSelect={handleAddressSelect}
            onBack={goBackFromAddressSelection}
            loading={state.loading}
          />
        )}

        {state.currentStep === 2 && (
          <PlansStep
            qualificationResult={state.qualificationResult}
            onSelectPlan={handlePlanSelect}
            onBack={goBack}
            loading={state.loading}
          />
        )}

        {state.currentStep === 3 && (
          <DetailsStep
            customer={state.customer}
            updateCustomer={updateCustomer}
            onSubmit={handleLeadSubmit}
            onBack={goBack}
            loading={state.loading}
            selectedPlan={state.selectedPlan}
            selectedAddress={state.selectedAddress}
            promoCode={state.promoCode}
            appliedPromo={state.appliedPromo}
            onApplyPromo={handleApplyPromo}
            onRemovePromo={handleRemovePromo}
            manualQualification={!state.isServiceable && !!state.qualificationResult?.manual_qualification_required}
          />
        )}

        {state.currentStep === 4 && (
          <ConfirmationStep
            leadReference={state.leadReference}
            message={state.confirmationMessage}
          />
        )}
      </main>
    </div>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;